import type {
  AgreementSpot,
  MyPagesSite,
  MyPagesStart,
  Permit,
  SpotShare,
} from "$apis/parkito2/index.ts";
import type { MyPagesStart as LegacyMyPagesStart } from "$apis/parkito/index.ts";
import { AgreementAccessModel, DebtorAccessModel } from "./mod.ts";

export default class MyPagesStartModel {
  constructor(private data: MyPagesStart) {}
  static fromLegacyApi(legacy: LegacyMyPagesStart): MyPagesStartModel {
    const newApi: MyPagesStart = {
      ...legacy,
      agreements: legacy.agreements.map((agg) => ({
        ...agg,
        agreement: {
          ...agg.agreement,
          permit: agg.agreement.permit as Permit,
        },
      })),
    };
    return new MyPagesStartModel(newApi);
  }

  get agreements() {
    return this.data.agreements.map((a) => new AgreementAccessModel(a));
  }
  get applicants() {
    return this.data.applicants;
  }
  get debtors() {
    return this.data.debtors.map((d) => new DebtorAccessModel(d));
  }
  get sites() {
    return this.data.sites;
  }
  get spotShares() {
    return this.data.spotShares;
  }

  getPermitAgreements() {
    return this.agreements
      .filter((a) => a.agreement.getProductKind() === "Permit")
      .map((a) => a.agreement);
  }

  getReservedAgreements() {
    return this.agreements
      .filter((a) => a.agreement.getProductKind() === "Reserved")
      .map((a) => a.agreement);
  }

  siteHasActiveAgreements(siteId: string): boolean {
    const found = this.agreements
      .filter((a) => a.agreement.data.siteId === siteId)
      .find((a) => a.agreement.isActive());
    return !!found;
  }

  findSite(siteId: string | undefined): MyPagesSite | undefined {
    return this.sites.find((s) => s.id === siteId);
  }

  getAgreementsForSite(siteId: string | undefined): AgreementAccessModel[] {
    return this.agreements.filter((a) => a.agreement.data.siteId === siteId);
  }

  getSpotSharesForSite(siteId: string | undefined): SpotShare[] {
    return this.spotShares.filter((ss) => ss.siteId === siteId);
  }

  findSpotShareForSpot(agreementSpotId: string): SpotShare | undefined {
    return this.spotShares.find((ss) => ss.spot.id === agreementSpotId);
  }

  findAgreementBySpot(
    agreementSpotId: string,
  ): AgreementAccessModel | undefined {
    return this.agreements.find((a) =>
      a.agreement.spots.find((s) => s.id === agreementSpotId)
    );
  }

  findAgreementById(
    agreementId: string | undefined,
  ): AgreementAccessModel | undefined {
    return this.agreements.find((a) => a.agreement.id === agreementId);
  }

  findAgreementSpot(agreementSpotId: string): AgreementSpot | undefined {
    return this.agreements.flatMap((a) => a.agreement.spots).find((
      s,
    ) => s.id === agreementSpotId);
  }

  findDebtorById(debtorId: string | undefined): DebtorAccessModel | undefined {
    return this.debtors.find((d) => d.debtor?.data.id === debtorId);
  }

  findDebtorByAgreementId(
    agreementId: string | undefined,
  ): DebtorAccessModel | undefined {
    const agreement = this.findAgreementById(agreementId)?.agreement;
    const debtorId = agreement?.debtor?.id;
    return this.findDebtorById(debtorId);
  }

  findSiteByAgreementId(
    agreementId: string | undefined,
  ): MyPagesSite | undefined {
    const agreement = this.findAgreementById(agreementId)?.agreement;
    const siteId = agreement?.data?.siteId;
    return this.findSite(siteId);
  }
}
